import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import styled from 'styled-components';
import { buttonResetStyle } from '../../layout/reset';
import { H3 } from '../Heading';
import { HStack } from '../HStack';
import { IconCancel, IconDirLeft } from '../Icon';
import { Portal, PortalNamespace } from '../Portal';
import {
  animationDuration,
  Body,
  Close,
  CloseBtn,
  Footer,
  Header,
  Heading,
  PanelWrapper,
  UiBlock,
  Wrapper,
} from './wrapper';

const PanelNavBackButton = styled.button`
  ${buttonResetStyle};
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

interface IPanelPortalNavBack {
  label: string;
  title?: string;
  onClick: () => void;
}

interface IPanelPortal {
  id?: string | null;
  title: string | null;
  onClose: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onNavBack?: IPanelPortalNavBack;
}

const Panel = ({ id, title, footer, onClose, children, onNavBack }: IPanelPortal) => {
  const t = useTranslation('common').t;
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [animate, setAnimate] = useState(true);

  const onCloseAnimate = useCallback(() => {
    setAnimate(false);
    setTimeout(() => onClose(), animationDuration);
  }, [onClose]);

  // Close on escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseAnimate();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseAnimate]);

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <CSSTransition
        nodeRef={nodeRef}
        in={animate}
        appear={true}
        timeout={animationDuration}
        classNames="panel"
      >
        <Wrapper ref={nodeRef}>
          <UiBlock title={t('closePanel') || ''} onClick={onCloseAnimate} />
          <PanelWrapper>
            <Header>
              <Heading>
                {onNavBack ? (
                  <H3
                    as={PanelNavBackButton}
                    onClick={onNavBack.onClick}
                    title={onNavBack.title || onNavBack.label}
                    $margin="none"
                  >
                    <HStack $spacing={1} $valign="center">
                      <IconDirLeft />
                      {onNavBack.label}
                    </HStack>
                  </H3>
                ) : (
                  <H3 as="h2" $margin="none">
                    {title}
                  </H3>
                )}
              </Heading>
              <Close>
                <CloseBtn
                  title={t('closePanel') || ''}
                  aria-label={t('closePanel') || undefined}
                  onClick={onCloseAnimate}
                >
                  <IconCancel />
                </CloseBtn>
              </Close>
            </Header>
            <Body>{children}</Body>
            {footer && <Footer>{footer}</Footer>}
          </PanelWrapper>
        </Wrapper>
      </CSSTransition>
    </Portal>
  );
};

export default Panel;
